import {
  Chip,
  DialogContentText,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  createStyles,
  fade,
  makeStyles,
  Theme
} from '@material-ui/core/styles';
import {
  AddRounded,
  DeleteForeverRounded,
  EditRounded,
  PersonAddRounded,
  PersonRounded,
  ShareRounded,
  VisibilityRounded
} from '@material-ui/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { RootState } from '../app/store';
import { useAppDispatch } from '../hooks/useAppRudux';
import {
  deleteProjectDocumentWithReasonAsync,
  ProjectDocument
} from '../pages/ProjectDocument/projectDocumentSlice';
import Card from './Card';
import ConfirmDelete from './ConfirmDelete';

interface ParamProps {
  page: number;
  limit: number;
}

interface DocumentCardProps<T> {
  renderDocuments: ProjectDocument[];
  params: T;
  setParams: (params: T) => void;
}

export default function DocumentCard<T extends ParamProps>({
  renderDocuments,
  params,
  setParams
}: DocumentCardProps<T>) {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { user } = useSelector((state: RootState) => state.userProfile);
  const { t } = useTranslation();

  const { data: disciplines } = useSelector(
    (state: RootState) => state.disciplines
  );

  const [openOption, setOpenOption] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<ProjectDocument | null>(null);
  const [openDelete, setOpenDelete] = useState<boolean>(false);

  const [reason, setReason] = useState<string>('');

  const isDocCreator: boolean = selectedDoc?.documentCreatorId === user?.id;

  function toggleDrawer(open: boolean, document?: ProjectDocument) {
    return (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      if (open && document) {
        setSelectedDoc(document);
      }
      setOpenOption(open);
    };
  }

  function handleClickDelete() {
    setOpenDelete(!openDelete);
    setReason('');
  }

  async function handleDeleteDocument() {
    if (selectedDoc) {
      const page =
        renderDocuments.length === 1
          ? Math.max(0, params.page - 1)
          : params.page;
      dispatch(
        deleteProjectDocumentWithReasonAsync({
          id: selectedDoc.id,
          reason,
          documentParams: { ...params, page }
        })
      );
      setParams({ ...params, page });
      setOpenDelete(false);
    }
  }

  return (
    <div>
      <ConfirmDelete
        open={openDelete}
        text={selectedDoc?.name}
        onCloseDelete={handleClickDelete}
        onDelete={handleDeleteDocument}
        additionalDisabled={reason.length === 0}
      >
        <DialogContentText
          className={classes.dialogContentText}
          id="reason-for-deletion"
        >
          {t('documentCard.reasonForDeletion') as string}<span className={classes.redColor}>*</span>
        </DialogContentText>
        <TextField
          className={classes.mb1}
          variant="outlined"
          color="primary"
          size="small"
          fullWidth
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder={t('documentCard.placeholderReasonForDeletion') as string}
          required
        />
      </ConfirmDelete>
      <Drawer anchor="bottom" open={openOption} onClose={toggleDrawer(false)}>
        <div
          className={classes.fullList}
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            <ListItem>
              <ListItemText
                primary={`${selectedDoc?.name} (${
                  disciplines.find(
                    (discipline) => discipline.id === selectedDoc?.disciplineId
                  )?.name
                })`}
              />
            </ListItem>
            <Divider className={classes.divider} />
            <ListItem
              component={NavLink}
              to={`/documents/${selectedDoc?.id}`}
              button
            >
              <ListItemIcon>
                <VisibilityRounded />
              </ListItemIcon>
              <ListItemText primary={t('documentCard.viewDocument') as string} />
            </ListItem>
            <ListItem
              component={NavLink}
              to={`/documents/${selectedDoc?.id}/share`}
              button
            >
              <ListItemIcon>
                <PersonAddRounded />
              </ListItemIcon>
              <ListItemText primary={t('documentCard.share') as string} />
            </ListItem>
            <ListItem
              component={NavLink}
              to={`/documents/${selectedDoc?.id}/edit`}
              button
            >
              <ListItemIcon>
                <EditRounded />
              </ListItemIcon>
              <ListItemText primary={t('documentCard.editDocument') as string} />
            </ListItem>
            <ListItem
              component={NavLink}
              to={`/document-revisions/create?documentId=${selectedDoc?.id}`}
              button
            >
              <ListItemIcon>
                <AddRounded />
              </ListItemIcon>
              <ListItemText primary={t('documentCard.newDocumentRevision') as string} />
            </ListItem>
            <Divider className={classes.divider} />
            <ListItem
              button
              disabled={!isDocCreator}
              onClick={handleClickDelete}
            >
              <ListItemIcon>
                <DeleteForeverRounded />
              </ListItemIcon>
              <ListItemText
                primary={t('documentCard.deleteAllRevision') as string}
                secondary={!isDocCreator && t('documentCard.onlyDocumentCreatorCanDelete') as string}
              />
            </ListItem>
          </List>
        </div>
      </Drawer>
      {renderDocuments?.map((document) => {
        const documentDiscipline = disciplines.find(
          (docDiscipline) => docDiscipline.id === document.disciplineId
        );
        const customDocument =
          document.projectDocumentCustomAccessControls.length > 0;
        return (
          <Card
            key={document.id}
            to={`/documents/${document.id}`}
            openDrawer={toggleDrawer(true, document)}
          >
            <div className={classes.item100}>
              <Typography variant="body1">
                {documentDiscipline && (
                  <Tooltip title={documentDiscipline?.cfihosName ?? ''} arrow>
                    <Chip
                      className={classes.chip}
                      component="span"
                      label={documentDiscipline.name}
                      variant="outlined"
                      size="small"
                    />
                  </Tooltip>
                )}
                {document.name}
              </Typography>
              <div className={classes.flexAlignCenter}>
                <Typography className={classes.docNoText} variant="subtitle2">
                  {document.no}
                </Typography>
                <Tooltip
                  title={`${document.accessControl.name} ${t('documentCard.document')}${
                    customDocument ? ` ${t('documentCard.withSpecificSharing')}` : ''
                  }`}
                  arrow
                >
                  <Chip
                    className={classes.chipIcon}
                    variant="outlined"
                    size="small"
                    icon={<ShareRounded />}
                    label={
                      <span className={classes.labelWithIcon}>
                        {document.accessControl.name}
                        {customDocument && (
                          <PersonAddRounded
                            className={classes.iconAtEnd}
                            color="action"
                          />
                        )}
                      </span>
                    }
                  />
                </Tooltip>
                {document.documentCreatorId === user?.id && (
                  <Tooltip title={t('documentCard.myDocument') as string} arrow>
                    <PersonRounded
                      className={classes.yourDocIcon}
                      color="action"
                    />
                  </Tooltip>
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fullList: {
      width: 'auto'
    },
    divider: {
      margin: theme.spacing(1, 0)
    },
    item100: {
      width: '100%'
    },
    chip: {
      margin: theme.spacing(0, 1, 0, -0.5),
      cursor: 'pointer',
      fontWeight: 'normal',
      fontSize: 13
    },
    flexAlignCenter: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    chipIcon: {
      marginRight: theme.spacing(0.5),
      cursor: 'pointer'
    },
    labelWithIcon: {
      display: 'flex',
      alignItems: 'center'
    },
    iconAtEnd: {
      marginLeft: theme.spacing(0.5),
      width: 18,
      height: 18
    },
    docNoText: {
      marginRight: theme.spacing(1)
    },
    yourDocIcon: {
      width: 24,
      height: 24,
      border: '1px solid',
      borderColor: fade(theme.palette.divider, 0.25),
      borderRadius: 16,
      padding: theme.spacing(0.25)
    },
    dialogContentText: {
      marginBottom: theme.spacing(1)
    },
    redColor: {
      color: theme.palette.error.main
    },
    mb1: {
      marginBottom: theme.spacing(1)
    }
  })
);
